/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'
import { Search, FolderOpen, Folder, FileCode, RefreshCw, Brain, ChevronRight, ChevronDown, Eye, EyeOff, ExternalLink } from 'lucide-react'
import { GeminiLogo, ClaudeLogo, GrokLogo, JudgeLogo } from '@/ui/components/LLMLogos'
import { LogsPanel } from '@/ui/components/LogsPanel'
import type { Project } from '@/lib/storage'
import { getDirectoryHandle, saveDirectoryHandle } from '@/lib/storage'
import { listFiles, readFile, fixDoubleEncoding, writeFile } from '@/lib/filesystem'

interface LeftSidebarProps {
  width: number
  currentProject: Project | null
  onFileDoubleClick?: (filePath: string, content: string) => void
  refreshTrigger?: number // Trigger refresh when this number changes
  highlightFile?: string | null // File path to highlight
}

export function LeftSidebar({ width, currentProject, onFileDoubleClick, refreshTrigger, highlightFile }: LeftSidebarProps) {
  const [repoFiles, setRepoFiles] = useState<string[]>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['.hivemind']))
  const [hasInitializedFolders, setHasInitializedFolders] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set())
  const [logsHeight, setLogsHeight] = useState(200) // Height of logs panel
  const [isResizingLogs, setIsResizingLogs] = useState(false)
  const [showHiddenFiles, setShowHiddenFiles] = useState(false) // Toggle for showing hidden files

  // When highlightFile prop changes, select that file and expand its folder
  useEffect(() => {
    if (highlightFile) {
      setSelectedFiles(new Set([highlightFile]))

      // Expand all parent folders
      const parts = highlightFile.split('/')
      const folders = new Set(expandedFolders)
      for (let i = 0; i < parts.length - 1; i++) {
        const folderPath = parts.slice(0, i + 1).join('/')
        folders.add(folderPath)
      }
      setExpandedFolders(folders)

      // Scroll to the file (with a small delay to ensure DOM is updated)
      setTimeout(() => {
        const fileElement = document.querySelector(`[data-file-path="${highlightFile}"]`)
        if (fileElement) {
          fileElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    }
  }, [highlightFile])

  // Function to load repository files
  const loadRepoFiles = async () => {
    if (!currentProject?.fileSystemPath) {
      setRepoFiles([])
      return
    }

    setIsLoadingFiles(true)
    try {
      const dirHandle = await getDirectoryHandle(currentProject.id)
      if (dirHandle) {
        const files = await listFiles(dirHandle)
        setRepoFiles(files.sort())
        console.log(`[Files] Loaded ${files.length} files from repository`)
      }
    } catch (error) {
      console.error('Failed to load repository files:', error)
    } finally {
      setIsLoadingFiles(false)
    }
  }

  // Load repository files when project changes
  useEffect(() => {
    loadRepoFiles()
  }, [currentProject?.id, currentProject?.fileSystemPath])

  // Manual refresh function
  const handleRefresh = () => {
    console.log('[Files] Manual refresh triggered')
    loadRepoFiles()
  }

  // Open folder in system file explorer
  const handleOpenInExplorer = async () => {
    if (!currentProject) return

    // If it's a local repo, try to open the existing folder
    if (currentProject.fileSystemPath) {
      try {
        const dirHandle = await getDirectoryHandle(currentProject.id)
        if (dirHandle) {
          // Use showDirectoryPicker to open a picker at this location
          // This will show the user where the folder is located
          await (window as any).showDirectoryPicker({
            id: currentProject.id,
            mode: 'readwrite',
            startIn: dirHandle
          })
        }
      } catch (error) {
        // User cancelled or browser doesn't support startIn
        console.log('[Files] Could not open folder:', error)
      }
      return
    }

    // If it's a GitHub project without a local folder, export it
    try {
      const confirmed = confirm(
        `Export "${currentProject.name}" to a local folder?\n\n` +
        `This will:\n` +
        `• Create a folder with all project files\n` +
        `• Convert this to a file system project\n` +
        `• Allow you to edit files with any tool\n\n` +
        `Continue?`
      )

      if (!confirmed) return

      // Ask user where to save
      const parentDirHandle = await (window as any).showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      })

      // Get a safe folder name from project name
      const folderName = currentProject.name.replace(/[^a-zA-Z0-9-_]/g, '-')

      // Check if folder already exists
      let folderExists = false
      try {
        await parentDirHandle.getDirectoryHandle(folderName, { create: false })
        folderExists = true
      } catch {
        folderExists = false
      }

      if (folderExists) {
        const overwrite = confirm(
          `A folder named "${folderName}" already exists.\n\n` +
          `⚠️ WARNING: Overwrite existing files?`
        )
        if (!overwrite) return
      }

      // Create the folder
      const repoDirHandle = await parentDirHandle.getDirectoryHandle(folderName, { create: true })
      console.log('[Files] Created export folder:', folderName)

      // Write all project files
      const files = currentProject.files || {}
      const fileCount = Object.keys(files).length
      let written = 0

      for (const [filePath, content] of Object.entries(files)) {
        try {
          await writeFile(repoDirHandle, filePath, content)
          written++
          if (written % 10 === 0) {
            console.log(`[Files] Exported ${written}/${fileCount} files...`)
          }
        } catch (error) {
          console.error(`[Files] Failed to write ${filePath}:`, error)
        }
      }

      console.log(`[Files] Exported ${written} files to ${folderName}`)

      // Store the directory handle
      await saveDirectoryHandle(currentProject.id, repoDirHandle)

      // Update the project to mark it as a file system project
      const { useProjectStore } = await import('@/ui/stores/project-store')
      const { currentProject: updatedProject, saveCurrentProject } = useProjectStore.getState()

      if (updatedProject) {
        updatedProject.fileSystemPath = folderName
        updatedProject.files = {} // Clear from memory, now on disk
        await saveCurrentProject()

        alert(`✅ Exported successfully!\n\n${written} files saved to:\n${folderName}\n\nYou can now add/edit files and open the folder in Explorer.`)

        // Refresh the file list
        loadRepoFiles()
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[Files] Export failed:', error)
        alert('Export failed: ' + error.message)
      }
    }
  }

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      console.log('[Files] Refresh triggered by parent')
      loadRepoFiles()
    }
  }, [refreshTrigger])

  // Handle logs panel resize
  useEffect(() => {
    if (!isResizingLogs) return

    const handleMouseMove = (e: MouseEvent) => {
      const sidebar = document.querySelector('aside')
      if (!sidebar) return

      const sidebarRect = sidebar.getBoundingClientRect()
      const newHeight = sidebarRect.bottom - e.clientY

      // Min 100px, max 500px
      setLogsHeight(Math.max(100, Math.min(500, newHeight)))
    }

    const handleMouseUp = () => {
      setIsResizingLogs(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizingLogs])

  // Helper to get icon for file
  const getFileIcon = (fileName: string) => {
    if (fileName.includes('gemini')) return { Icon: GeminiLogo, color: 'text-purple-400' }
    if (fileName.includes('claude')) return { Icon: ClaudeLogo, color: 'text-orange-400' }
    if (fileName.includes('grok')) return { Icon: GrokLogo, color: 'text-green-400' }
    if (fileName.includes('judge')) return { Icon: JudgeLogo, color: 'text-yellow-400' }
    if (fileName.includes('orchestrator')) return { Icon: Brain, color: 'text-accent' }
    return { Icon: FileCode, color: 'text-blue-400' }
  }

  // Filter files based on search query
  const filterFiles = (files: string[], query: string) => {
    if (!query.trim()) return files
    const lowerQuery = query.toLowerCase()
    return files.filter((filePath) => filePath.toLowerCase().includes(lowerQuery))
  }

  // Get all parent folders for a file path
  const getParentFolders = (filePath: string): string[] => {
    const parts = filePath.split('/')
    const folders: string[] = []
    for (let i = 0; i < parts.length - 1; i++) {
      folders.push(parts.slice(0, i + 1).join('/'))
    }
    return folders
  }

  // Auto-expand folders when filtering
  useEffect(() => {
    if (searchFilter.trim()) {
      // Get all matching files
      const matchingFiles = filterFiles(repoFiles, searchFilter)

      // Get all parent folders of matching files
      const foldersToExpand = new Set<string>()
      matchingFiles.forEach(filePath => {
        getParentFolders(filePath).forEach(folder => {
          foldersToExpand.add(folder)
        })
      })

      // Expand all parent folders
      setExpandedFolders(foldersToExpand)
    } else if (!hasInitializedFolders && repoFiles.length > 0) {
      // On initial load, expand first-level folders
      const firstLevelFolders = new Set<string>(['.hivemind'])
      repoFiles.forEach(filePath => {
        const firstFolder = filePath.split('/')[0]
        if (firstFolder && filePath.includes('/')) {
          firstLevelFolders.add(firstFolder)
        }
      })
      setExpandedFolders(firstLevelFolders)
      setHasInitializedFolders(true)
    } else if (searchFilter.trim() === '' && hasInitializedFolders) {
      // When filter is cleared after initialization, keep first-level folders expanded
      const firstLevelFolders = new Set<string>(['.hivemind'])
      repoFiles.forEach(filePath => {
        const firstFolder = filePath.split('/')[0]
        if (firstFolder && filePath.includes('/')) {
          firstLevelFolders.add(firstFolder)
        }
      })
      setExpandedFolders(firstLevelFolders)
    }
  }, [searchFilter, repoFiles, hasInitializedFolders])

  // Organize files into a tree structure
  const organizeFilesIntoTree = (files: string[]) => {
    const tree: Record<string, any> = {}

    files.forEach((filePath) => {
      const parts = filePath.split('/')
      let current = tree

      parts.forEach((part, index) => {
        if (!current[part]) {
          current[part] = index === parts.length - 1 ? null : {}
        }
        if (index < parts.length - 1) {
          current = current[part]
        }
      })
    })

    return tree
  }

  const toggleFolder = (folderPath: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderPath)) {
        next.delete(folderPath)
      } else {
        next.add(folderPath)
      }
      return next
    })
  }

  const handleFileClick = (e: React.MouseEvent, filePath: string) => {
    if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd+Click: Toggle selection
      setSelectedFiles((prev) => {
        const next = new Set(prev)
        if (next.has(filePath)) {
          next.delete(filePath)
        } else {
          next.add(filePath)
        }
        return next
      })
    } else if (e.shiftKey && selectedFiles.size > 0) {
      // Shift+Click: Select range (simplified - just add to selection)
      setSelectedFiles((prev) => new Set([...prev, filePath]))
    } else {
      // Regular click: Select only this file
      setSelectedFiles(new Set([filePath]))
    }
  }

  const handleFileDoubleClick = async (filePath: string) => {
    if (!onFileDoubleClick || !currentProject) return

    try {
      let content: string | null = null

      // Try to get content from filesystem if it's a repo project
      if (currentProject.fileSystemPath) {
        const dirHandle = await getDirectoryHandle(currentProject.id)
        if (dirHandle) {
          content = await readFile(dirHandle, filePath)
        }
      } else {
        // Get content from project.files for non-repo projects
        const rawContent = currentProject.files[filePath] || null
        content = rawContent ? fixDoubleEncoding(rawContent) : null
      }

      if (content !== null) {
        onFileDoubleClick(filePath, content)
      } else {
        console.error(`Failed to read file: ${filePath}`)
      }
    } catch (error) {
      console.error(`Failed to open file ${filePath}:`, error)
    }
  }

  const handleFileDragStart = (e: React.DragEvent, filePath: string) => {
    if (!currentProject) {
      return
    }

    // If dragging a file that's not selected, select only that file
    if (!selectedFiles.has(filePath)) {
      setSelectedFiles(new Set([filePath]))
    }

    // Get all files to drag (either selected files or just this one)
    const filesToDrag = selectedFiles.has(filePath) ? Array.from(selectedFiles) : [filePath]

    // Set effectAllowed to allow copying
    e.dataTransfer.effectAllowed = 'copy'

    // Set placeholder data immediately (synchronously required for drag/drop to work)
    e.dataTransfer.setData('text/plain', `Loading ${filesToDrag.length} file(s)...`)
    e.dataTransfer.setData('application/x-hivemind-file-paths', JSON.stringify(filesToDrag))

    console.log('[LeftSidebar] Drag start for files:', filesToDrag)

    // Load file contents based on project type
    if (currentProject.fileSystemPath) {
      // File system project - load from directory handle
      getDirectoryHandle(currentProject.id).then(async dirHandle => {
        if (dirHandle) {
          const fileDataArray: Array<{ filePath: string; content: string }> = []

          for (const path of filesToDrag) {
            try {
              const content = await readFile(dirHandle, path)
              if (content !== null) {
                fileDataArray.push({ filePath: path, content })
              }
            } catch (error) {
              console.error(`Failed to read file ${path}:`, error)
            }
          }

          // Store in window for drop handler to access
          ;(window as any).__draggedFileData = fileDataArray
          console.log('[LeftSidebar] Loaded file data from file system:', fileDataArray.length, 'files')
        }
      }).catch(error => {
        console.error('Failed to get directory handle:', error)
      })
    } else {
      // IndexedDB project - get content from project.files
      const fileDataArray: Array<{ filePath: string; content: string }> = []

      for (const path of filesToDrag) {
        const content = currentProject.files[path]
        if (content) {
          fileDataArray.push({ filePath: path, content })
        }
      }

      // Store in window for drop handler to access
      ;(window as any).__draggedFileData = fileDataArray
      console.log('[LeftSidebar] Loaded file data from IndexedDB:', fileDataArray.length, 'files')
    }
  }

  // Highlight matching text in a string
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return <span>{text}</span>

    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    const index = lowerText.indexOf(lowerQuery)

    if (index === -1) return <span>{text}</span>

    const before = text.slice(0, index)
    const match = text.slice(index, index + query.length)
    const after = text.slice(index + query.length)

    return (
      <span>
        {before}
        <span className="bg-accent/30 text-accent font-semibold">{match}</span>
        {after}
      </span>
    )
  }

  // Render file tree recursively
  const renderFileTree = (tree: Record<string, any>, basePath: string = '', depth: number = 0) => {
    return Object.entries(tree).map(([name, children]) => {
      const fullPath = basePath ? `${basePath}/${name}` : name
      const isFolder = children !== null
      const isExpanded = expandedFolders.has(fullPath)
      const isHivemindFolder = fullPath === '.hivemind'

      if (isFolder) {
        const ChevronIcon = isExpanded ? ChevronDown : ChevronRight
        const FolderIcon = isExpanded ? FolderOpen : Folder

        return (
          <div key={fullPath}>
            <div
              style={{ marginLeft: `${depth * 12}px` }}
              className={`flex items-center gap-1 py-1 hover:bg-background-hover rounded px-1 cursor-pointer ${
                isHivemindFolder ? 'bg-accent/10 border border-accent/30' : ''
              }`}
              onClick={() => toggleFolder(fullPath)}
            >
              <ChevronIcon className="w-3 h-3 text-foreground-muted flex-shrink-0" />
              <FolderIcon className={`w-3 h-3 flex-shrink-0 ${isHivemindFolder ? 'text-accent' : 'text-foreground-muted'}`} />
              <span className={`text-xs truncate ${isHivemindFolder ? 'text-accent font-semibold' : ''}`}>
                {highlightMatch(name, searchFilter)}
              </span>
            </div>
            {isExpanded && renderFileTree(children, fullPath, depth + 1)}
          </div>
        )
      } else {
        const { Icon, color } = getFileIcon(fullPath)
        const isSelected = selectedFiles.has(fullPath)
        return (
          <div
            key={fullPath}
            data-file-path={fullPath}
            style={{ marginLeft: `${depth * 12}px` }}
            className={`flex items-center gap-1 py-1 hover:bg-background-hover rounded px-1 cursor-pointer select-none ${
              isSelected ? 'bg-accent/20 border border-accent/50' : ''
            }`}
            title={`${fullPath} (double-click to view)`}
            draggable
            onClick={(e) => handleFileClick(e, fullPath)}
            onDoubleClick={() => handleFileDoubleClick(fullPath)}
            onDragStart={(e) => handleFileDragStart(e, fullPath)}
          >
            <Icon className={`w-3 h-3 ${color}`} />
            <span className="truncate text-xs">{highlightMatch(name, searchFilter)}</span>
          </div>
        )
      }
    })
  }

  // Apply search filter to repo files
  let filteredRepoFiles = filterFiles(repoFiles, searchFilter)

  // Filter out hidden files if showHiddenFiles is false
  if (!showHiddenFiles) {
    filteredRepoFiles = filteredRepoFiles.filter(filePath => {
      // Check if any part of the path starts with a dot (hidden file/folder)
      const parts = filePath.split('/')
      return !parts.some(part => part.startsWith('.'))
    })
  }

  const fileTree = filteredRepoFiles.length > 0 ? organizeFilesIntoTree(filteredRepoFiles) : {}

  // Organize Hivemind files (from project.files) into a tree structure
  const organizeNonRepoFiles = (files: Record<string, string>) => {
    let filePaths = Object.keys(files)

    // Filter out hidden files if showHiddenFiles is false
    if (!showHiddenFiles) {
      filePaths = filePaths.filter(filePath => {
        // Check if any part of the path starts with a dot (hidden file/folder)
        const parts = filePath.split('/')
        return !parts.some(part => part.startsWith('.'))
      })
    }

    return organizeFilesIntoTree(filePaths)
  }

  const nonRepoFileTree = currentProject?.files && !currentProject.fileSystemPath
    ? organizeNonRepoFiles(currentProject.files)
    : {}

  return (
    <aside
      style={{ width: `${width}px` }}
      className="bg-background-secondary border-r border-border flex flex-col flex-shrink-0"
    >
      <div className="dock-header">
        <h2 className="text-sm font-title">FILES</h2>
      </div>

      <div className="px-2 py-2 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-foreground-muted" />
          <input
            type="text"
            placeholder="Filter files..."
            className="input h-8 pl-7 text-xs w-full"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>
        <button
          className={`px-2 py-1 text-xs flex items-center gap-1 rounded border transition-colors ${
            showHiddenFiles
              ? 'bg-accent/20 border-accent/50 text-accent'
              : 'btn-secondary'
          }`}
          onClick={() => setShowHiddenFiles(!showHiddenFiles)}
          title={showHiddenFiles ? 'Hide hidden files (files starting with .)' : 'Show hidden files (files starting with .)'}
        >
          {showHiddenFiles ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
        </button>
        <button
          className="btn-secondary px-2 py-1 text-xs flex items-center gap-1"
          onClick={handleRefresh}
          disabled={isLoadingFiles}
          title="Refresh file list"
        >
          <RefreshCw className={`w-3 h-3 ${isLoadingFiles ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 overflow-auto px-2 text-sm space-y-0.5">
        {currentProject ? (
          <>
            <div className="flex items-center justify-between gap-1 py-1 hover:bg-background-hover rounded px-1 group">
              <div className="flex items-center gap-1">
                <FolderOpen className="w-4 h-4 text-foreground-muted" />
                <span>/{currentProject.name}</span>
              </div>
              <button
                onClick={handleOpenInExplorer}
                className="opacity-50 group-hover:opacity-100 transition-opacity p-1 hover:bg-background-tertiary rounded"
                title={currentProject.fileSystemPath ? "Open folder in File Explorer" : "This GitHub project is stored in your browser. Click to save to a local folder (coming soon)"}
              >
                <ExternalLink className="w-3 h-3 text-foreground-muted" />
              </button>
            </div>
            <div className="ml-2 space-y-0.5">
              {/* Show search results count if filtering */}
              {searchFilter && currentProject.fileSystemPath && (
                <div className="text-xs text-foreground-muted italic py-1 px-1">
                  {filteredRepoFiles.length} {filteredRepoFiles.length === 1 ? 'file' : 'files'} found
                </div>
              )}

              {/* Show repository files if this is a repo project */}
              {currentProject.fileSystemPath && filteredRepoFiles.length > 0 ? (
                <div className="space-y-0.5">
                  {renderFileTree(fileTree)}
                </div>
              ) : currentProject.fileSystemPath && searchFilter && repoFiles.length > 0 ? (
                <div className="text-foreground-muted italic text-xs py-2">
                  No files match "{searchFilter}"
                </div>
              ) : currentProject.fileSystemPath && isLoadingFiles ? (
                <div className="text-foreground-muted italic text-xs py-2">
                  Loading repository files...
                </div>
              ) : !currentProject.fileSystemPath && Object.keys(currentProject.files || {}).length === 0 ? (
                <div className="text-foreground-muted italic text-xs py-2">
                  No files yet. Save chats to create files.
                </div>
              ) : !currentProject.fileSystemPath && Object.keys(nonRepoFileTree).length > 0 ? (
                <div className="space-y-0.5">
                  {renderFileTree(nonRepoFileTree)}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className="text-foreground-muted italic text-xs py-2 px-1">
            No project selected. Create or open a project.
          </div>
        )}
      </div>

      {/* Resizable Logs Panel */}
      <div
        className="flex-shrink-0 px-2 pb-2 flex flex-col"
        style={{ height: `${logsHeight}px` }}
      >
        {/* Resize Handle */}
        <div
          className="h-1 cursor-row-resize hover:bg-accent/20 active:bg-accent/30 -mx-2 mb-1"
          onMouseDown={() => setIsResizingLogs(true)}
          title="Drag to resize logs panel"
        />

        {/* Logs Content */}
        <div className="flex-1 overflow-hidden">
          <LogsPanel />
        </div>
      </div>
    </aside>
  )
}

